import * as T from 'three';
import type { SceneKit } from '@london-life/game/scene/kit';
import type { Instancer } from '@london-life/game/city/instanced';

// the library's models for the map (packages/game/models): one is picked for each spot, always the same one
// (a light set: the whole map is about 1.5 MB the first time, then cached)
const SUBURBAN = 'abcdfhjnot'.split('').map((letter) => `suburban/building-type-${letter}`);
const COMMERCIAL = 'abcdfgh'.split('').map((letter) => `commercial/building-${letter}`);
const WIDE = ['commercial/building-e', 'commercial/building-k'];
const SKYSCRAPERS = 'abd'.split('').map((letter) => `commercial/building-skyscraper-${letter}`);
const TREES = ['nature/tree_default', 'nature/tree_oak', 'nature/tree_detailed', 'nature/tree_fat'];
const CARS = ['cars/sedan', 'cars/taxi', 'cars/van'];
// London colours: red brick and grey slate houses with some blue; white, sandy and red brick offices
const HOUSE_SHEETS = ['b', 'c', 'b', 'a', 'c'].map((letter) => `suburban/Textures/variation-${letter}.png`);
const OFFICE_SHEETS = [undefined, 'commercial/Textures/variation-a.png', 'commercial/Textures/variation-b.png'];
const sheetFor = (model: string, x: number, z: number) =>
  model.startsWith('suburban/')
    ? HOUSE_SHEETS[Math.abs(Math.round(x * 2.9 + z * 1.7)) % HOUSE_SHEETS.length]
    : model.startsWith('commercial/building-') && !model.includes('skyscraper')
      ? OFFICE_SHEETS[Math.abs(Math.round(x * 2.9 + z * 1.7)) % OFFICE_SHEETS.length]
      : undefined;
// every model and colour sheet the map can use (the tests check each is in the library)
export const MAP_MODELS = [
  ...SUBURBAN,
  ...COMMERCIAL,
  ...WIDE,
  ...SKYSCRAPERS,
  ...TREES,
  ...CARS,
  'street/light-square',
];
export const MAP_SHEETS = [...HOUSE_SHEETS, ...OFFICE_SHEETS].filter((sheet): sheet is string => Boolean(sheet));
const pick = (list: string[], x: number, z: number) => list[Math.abs(Math.round(x * 3.7 + z * 5.3)) % list.length]!;

// shared the small reusable models that fill every district; with an instancer, the library's models replace
// the blocks once they have loaded
export function createProps(kit: SceneKit, group: T.Group, models?: Instancer) {
  const box = (w: number, h: number, d: number, color: string, x: number, y: number, z: number, rotation = 0) =>
    kit.box(w, h, d, color, x, y, z, group, rotation);

  // grow a round low-poly tree
  const tree = (x: number, z: number, size = 1, canopy = '#5f9b52') => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    group.add(b);
    kit.cylinder(0.16 * size, 1.1 * size, '#7d5a3c', 0, 0.55 * size, 0, b, 0.12 * size, 6);
    kit.sphere(1.05 * size, canopy, 0, 1.75 * size, 0, b);
    kit.sphere(0.7 * size, canopy, 0.45 * size, 2.3 * size, -0.2 * size, b);
    models?.add(pick(TREES, x, z), b, { w: 2.2 * size, d: 2.2 * size, h: 3 * size });
  };

  // lay a road with a dashed centre line and pale kerbs
  const road = (x: number, z: number, length: number, rotation = 0, width = 4.2) => {
    box(length, 0.12, width, '#5b6673', x, 0.2, z, rotation);
    box(length, 0.14, 0.25, '#dfe5ea', x, 0.2, z, rotation);
    const dash = new T.Object3D();
    dash.position.set(x, 0, z);
    dash.rotation.y = rotation;
    group.add(dash);
    for (let d = -length / 2 + 2; d < length / 2 - 1; d += 4) kit.box(1.6, 0.02, 0.18, '#f3f3e8', d, 0.28, 0, dash);
    for (const side of [-1, 1]) kit.box(length, 0.14, 0.25, '#dfe5ea', 0, 0.2, (side * width) / 2, dash);
  };
  const roundabout = (x: number, z: number, r = 4.5) => {
    kit.cylinder(r, 0.14, '#5b6673', x, 0.2, z, group, r, 18);
    kit.cylinder(r - 2.1, 0.22, '#7fb36a', x, 0.24, z, group, r - 2.1, 14);
    tree(x, z, 0.8);
  };

  // build a simple building with a roof slab and window strips
  const building = (
    x: number,
    z: number,
    w: number,
    d: number,
    h: number,
    wall: string,
    roof = '#8b8580',
    rotation = 0,
  ) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(w, h, d, wall, 0, h / 2 + 0.25, 0, b);
    kit.box(w + 0.3, 0.3, d + 0.3, roof, 0, h + 0.4, 0, b);
    for (let floor = 1.4; floor < h - 0.4; floor += 1.35) {
      kit.box(w - 0.8, 0.55, 0.06, '#44596b', 0, floor + 0.25, d / 2 + 0.03, b);
      kit.box(w - 0.8, 0.55, 0.06, '#44596b', 0, floor + 0.25, -d / 2 - 0.03, b);
      kit.box(0.06, 0.55, d - 0.8, '#44596b', w / 2 + 0.03, floor + 0.25, 0, b);
      kit.box(0.06, 0.55, d - 0.8, '#44596b', -w / 2 - 0.03, floor + 0.25, 0, b);
    }
    // big or tall: an office or a shop block; otherwise a house
    const list = w * d >= 40 || h > 4.5 ? (w / d > 1.6 || d / w > 1.6 ? WIDE : COMMERCIAL) : SUBURBAN;
    const model = pick(list, x, z);
    models?.add(model, b, { w, d, h: h + 0.6 }, 0, sheetFor(model, x, z));
    return b;
  };
  // give a small home a pitched roof
  const house = (x: number, z: number, w: number, d: number, wall: string, roof: string, rotation = 0, h = 1.6) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(w, h, d, wall, 0, h / 2 + 0.25, 0, b);
    kit.cone(Math.max(w, d) * 0.72, 1.1, roof, 0, h + 0.8, 0, b);
    kit.box(0.5, 0.4, 0.06, '#44596b', 0, h / 2 + 0.3, d / 2 + 0.03, b);
    const model = pick(SUBURBAN, x, z);
    models?.add(model, b, { w: w * 1.3, d: d * 1.3, h: h + 1.3 }, 0, sheetFor(model, x, z));
    return b;
  };
  // raise a glass tower for the business district
  const tower = (x: number, z: number, w: number, d: number, h: number, glass = '#7fa7c4', pyramid = false) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    group.add(b);
    kit.box(w, h, d, glass, 0, h / 2 + 0.25, 0, b);
    for (let floor = 2; floor < h - 0.5; floor += 2.2) kit.box(w + 0.08, 0.22, d + 0.08, '#e9f1f6', 0, floor, 0, b);
    // One Canada Square keeps its own pyramid top: the city's landmark
    if (pyramid) kit.cone(w * 0.72, 3.2, '#e2e8ee', 0, h + 1.85, 0, b, 4);
    else {
      kit.box(w * 0.6, 0.6, d * 0.6, '#52657a', 0, h + 0.55, 0, b);
      models?.add(pick(SKYSCRAPERS, x, z), b, { w, d, h: h + 0.9 });
    }
  };

  // stand an advert on two legs
  const board = (x: number, z: number, w: number, h: number, art: HTMLCanvasElement, rotation = 0, lift = 1.6) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(w + 0.3, h + 0.3, 0.3, '#2b3542', 0, lift + h / 2, 0, b);
    const picture = kit.picture(art, w, h, 0, lift + h / 2, 0.18, b, 0, 0);
    for (const side of [-1, 1]) kit.box(0.16, lift, 0.16, '#4e5a66', side * (w / 2 - 0.4), lift / 2, 0, b);
    return { object: b, picture };
  };
  const lamp = (x: number, z: number) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    group.add(b);
    kit.box(0.1, 2.2, 0.1, '#5b6670', 0, 1.3, 0, b);
    kit.sphere(0.18, '#fff2c8', 0, 2.45, 0, b);
    models?.add('street/light-square', b, { w: 1, d: 1, h: 2.7 });
  };

  // park a plane on the apron
  const plane = (x: number, z: number, rotation = 0, body = '#f4f6f8', tail = '#2f6fb3') => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    const fuselage = kit.cylinder(0.8, 8, body, 0, 1.3, 0, b, 0.55, 8);
    fuselage.rotation.z = Math.PI / 2;
    kit.box(1.2, 0.18, 9, body, -0.3, 1.2, 0, b);
    kit.box(0.6, 2.2, 0.18, tail, -3.4, 2.2, 0, b);
    kit.box(0.5, 0.14, 3.4, tail, -3.5, 1.4, 0, b);
    kit.box(0.6, 0.16, 0.9, '#2f6fb3', 2.8, 1.5, 0, b);
    for (const side of [-1, 1])
      kit.cylinder(0.5, 1.4, '#5a6673', 0.3, 0.95, side * 2.4, b, 0.5, 8).rotation.z = Math.PI / 2;
  };
  const tank = (x: number, z: number, r = 4, h = 2.6) => {
    kit.cylinder(r, h, '#eef0f2', x, h / 2 + 0.25, z, group, r, 16);
    kit.cylinder(r + 0.1, 0.3, '#b9c0c6', x, h + 0.4, z, group, r - 0.4, 16);
  };
  const crane = (x: number, z: number, rotation = 0, h = 9) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(0.7, h, 0.7, '#e9b526', 0, h / 2 + 0.25, 0, b);
    kit.box(13, 0.5, 0.5, '#e9b526', 3.5, h + 0.3, 0, b);
    kit.box(0.08, 4, 0.08, '#333', 8.5, h - 1.7, 0, b);
    kit.box(0.9, 0.9, 0.9, '#3c4654', 8.5, h - 3.9, 0, b);
  };

  // keep a few London vehicles still until traffic arrives
  const bus = (x: number, z: number, rotation = 0) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(5.2, 2.4, 1.9, '#cf3a2f', 0, 1.45, 0, b);
    kit.box(5.2, 0.5, 1.5, '#8c2a22', 0, 2.85, 0, b);
    for (const level of [1.1, 2.1])
      for (const side of [-1, 1]) kit.box(4.4, 0.5, 0.05, '#2d4253', 0, level, side * 0.97, b);
    for (const dx of [-1.6, 1.6])
      for (const dz of [-0.7, 0.7])
        kit.cylinder(0.32, 0.25, '#262b31', dx, 0.32, dz, b, 0.32, 8).rotation.x = Math.PI / 2;
  };
  const car = (x: number, z: number, rotation = 0, color = '#1f242b') => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    kit.box(2.6, 0.7, 1.3, color, 0, 0.62, 0, b);
    kit.box(1.4, 0.55, 1.15, '#55697a', -0.1, 1.2, 0, b);
    for (const dx of [-0.85, 0.85])
      for (const dz of [-0.6, 0.6])
        kit.cylinder(0.24, 0.2, '#262b31', dx, 0.28, dz, b, 0.24, 8).rotation.x = Math.PI / 2;
    models?.add(pick(CARS, x, z), b, { w: 2.8, d: 1.4 }, Math.PI / 2);
  };
  const train = (x: number, z: number, rotation = 0) => {
    const b = new T.Object3D();
    b.position.set(x, 0, z);
    b.rotation.y = rotation;
    group.add(b);
    for (const dx of [-5, 0, 5]) {
      kit.box(4.6, 1.5, 1.6, '#e8ecef', dx, 1.05, 0, b);
      kit.box(4.6, 0.4, 1.62, '#1c7fc0', dx, 0.55, 0, b);
      kit.box(4, 0.4, 0.05, '#2d4253', dx, 1.2, 0.82, b);
      kit.box(4, 0.4, 0.05, '#2d4253', dx, 1.2, -0.82, b);
    }
    return b;
  };
  // stand a small person on the pavement
  const person = (x: number, z: number, top: string, skin = '#8d5a3b') => {
    kit.cylinder(0.22, 0.55, top, x, 0.55, z, group, 0.22, 6);
    kit.sphere(0.2, skin, x, 1.0, z, group);
  };

  return {
    box,
    tree,
    road,
    roundabout,
    building,
    house,
    tower,
    board,
    lamp,
    plane,
    tank,
    crane,
    bus,
    car,
    train,
    person,
  };
}

// paint an advert for a board or a plot
export function drawAdvert(text: string, background: string, foreground: string, width = 256, height = 128) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d')!;
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);
  context.fillStyle = foreground;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  context.font = `800 ${Math.round(height * 0.22)}px "Plus Jakarta Sans", Arial`;
  for (const word of words) {
    const trial = line ? `${line} ${word}` : word;
    if (context.measureText(trial).width > width * 0.86 && line) {
      lines.push(line);
      line = word;
    } else line = trial;
  }
  lines.push(line);
  const step = height * 0.24;
  lines.forEach((entry, index) =>
    context.fillText(entry, width / 2, height / 2 + (index - (lines.length - 1) / 2) * step, width * 0.9),
  );
  return canvas;
}

// a player's picture on a board: fitted whole (never cropped) on the board's dark screen, drawn at the board's
// own shape (`width` across)
export function drawBoardPicture(
  image: CanvasImageSource & { width: number; height: number },
  w: number,
  h: number,
  width = 512,
) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.round((width * h) / w);
  const context = canvas.getContext('2d')!;
  context.fillStyle = '#0e1830';
  context.fillRect(0, 0, canvas.width, canvas.height);
  const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
  const drawW = image.width * scale,
    drawH = image.height * scale;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, (canvas.width - drawW) / 2, (canvas.height - drawH) / 2, drawW, drawH);
  return canvas;
}

// the river plots as one picture (contracts RIVER_PLOTS): free plots are open water marked "FREE", a plot that
// is held shows its picture fitted inside it (never cropped), or "SOLD" while it waits to be checked
export type PlotPicture = { cell: number; span: number; image: CanvasImageSource | null };
export const PLOT_TILE = 96;
export function drawRiverPlots(columns: number, rows: number, taken: PlotPicture[]) {
  const tile = PLOT_TILE,
    canvas = document.createElement('canvas');
  canvas.width = columns * tile;
  canvas.height = rows * tile;
  const context = canvas.getContext('2d')!;
  context.fillStyle = '#9ccbe0';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const held = new Set<number>();
  for (const { cell, span } of taken)
    for (let r = 0; r < span; r++) for (let c = 0; c < span; c++) held.add(cell + r * columns + c);
  for (let cell = 0; cell < columns * rows; cell++) {
    if (held.has(cell)) continue;
    const x = (cell % columns) * tile,
      y = Math.floor(cell / columns) * tile;
    context.strokeStyle = 'rgba(255,255,255,0.75)';
    context.lineWidth = 3;
    context.strokeRect(x + 6, y + 6, tile - 12, tile - 12);
    context.fillStyle = 'rgba(255,255,255,0.85)';
    context.font = '800 18px "Plus Jakarta Sans", Arial';
    context.fillText('FREE', x + tile / 2, y + tile / 2);
  }
  for (const { cell, span, image } of taken) {
    const x = (cell % columns) * tile + 4,
      y = Math.floor(cell / columns) * tile + 4,
      size = span * tile - 8;
    context.fillStyle = '#0e1830';
    context.fillRect(x, y, size, size);
    if (image) {
      const { width, height } = image as { width: number; height: number };
      const scale = Math.min(size / width, size / height);
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        image,
        x + (size - width * scale) / 2,
        y + (size - height * scale) / 2,
        width * scale,
        height * scale,
      );
    } else {
      context.fillStyle = '#f6b83d';
      context.font = `800 ${14 + span * 6}px "Plus Jakarta Sans", Arial`;
      context.fillText('SOLD', x + size / 2, y + size / 2);
    }
  }
  return canvas;
}
